import React, { useState, useMemo } from "react";
import {
  useCreateStagingTestCasesMutation,
  useCreateProdTestCasesMutation,
} from "../../../../app/api/console/scanTemplateSyncApiSlice";
import { useGetWebsitesListQuery } from "../../../../app/api/platform/platformWebsitesApiSlice";

const STARTER_TEST_CASE = {
  id: "tc_new_1",
  name: "",
  category: "",
  url_placeholder: "root_url",
  activated: true,
  items: [
    { item_type: "agent_multistep", title: "", objective: "" },
    { item_type: "verify", title: "", objective: "" },
  ],
};

const AddWebsiteTestCasesSection = ({ templateId, environment, hasBaseTestCases, websiteTestCaseIds }) => {
  const [mode, setMode] = useState(null);
  const [inputMode, setInputMode] = useState("json");
  const [newTestCasesText, setNewTestCasesText] = useState("");
  const [selectedWebsiteId, setSelectedWebsiteId] = useState("");
  const [websiteSearch, setWebsiteSearch] = useState("");

  const { data: websites = [], isLoading: isLoadingWebsites } = useGetWebsitesListQuery({ useProd: environment === "prod" });

  const [createStagingTestCases, { isLoading: isCreatingStagingTestCases }] = useCreateStagingTestCasesMutation();
  const [createProdTestCases, { isLoading: isCreatingProdTestCases }] = useCreateProdTestCasesMutation();
  const isCreating = environment === "staging" ? isCreatingStagingTestCases : isCreatingProdTestCases;

  // Not memoised on purpose: `websites` comes straight from the RTK Query cache (stable
  // reference) and the `[]` fallback only applies while the list is loading; wrapping it
  // in useMemo would change its identity semantics for the `filteredWebsites` memo below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const websitesList = Array.isArray(websites) ? websites : [];

  const filteredWebsites = useMemo(() => {
    return websitesList
      .filter((w) => !(websiteTestCaseIds || []).includes(w.website_id))
      .filter((w) => !websiteSearch || w.website_name?.toLowerCase().includes(websiteSearch.toLowerCase()) || w.website_url?.toLowerCase().includes(websiteSearch.toLowerCase()));
  }, [websitesList, websiteTestCaseIds, websiteSearch]);

  const selectedWebsite = websitesList.find(w => w.website_id === selectedWebsiteId);

  const handleCreate = async () => {
    if (!newTestCasesText.trim()) return;
    if (mode === "website" && !selectedWebsiteId) return;
    let parsed;
    try {
      parsed = JSON.parse(newTestCasesText);
    } catch {
      alert("Invalid JSON. Please fix the syntax and try again.");
      return;
    }
    const createFn = environment === "staging" ? createStagingTestCases : createProdTestCases;
    await createFn({
      templateId,
      testCases: { website_id: mode === "base" ? null : selectedWebsiteId, test_cases: parsed },
    });
    setNewTestCasesText("");
    setSelectedWebsiteId("");
    setWebsiteSearch("");
    setMode(null);
  };

  const handleCreateEmpty = async () => {
    if (mode === "website" && !selectedWebsiteId) return;
    const createFn = environment === "staging" ? createStagingTestCases : createProdTestCases;
    await createFn({
      templateId,
      testCases: { website_id: mode === "base" ? null : selectedWebsiteId, test_cases: [STARTER_TEST_CASE] },
    });
    setSelectedWebsiteId("");
    setWebsiteSearch("");
    setMode(null);
  };

  const handleCancel = () => {
    setNewTestCasesText("");
    setSelectedWebsiteId("");
    setWebsiteSearch("");
    setMode(null);
  };

  if (mode) {
    return (
      <div className="rounded-xl border-2 border-dashed border-violet-300 bg-violet-50/30 p-5">
        <h4 className="font-semibold text-gray-900 mb-4">
          {mode === "base" ? "Add Base Test Cases" : "Add Website-Specific Test Cases"}
        </h4>

        {mode === "website" && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
            {selectedWebsiteId ? (
              <div className="flex items-center gap-2 p-2 bg-white rounded-lg border">
                <div className="w-6 h-6 rounded bg-amber-100 flex items-center justify-center text-xs font-bold text-amber-700">
                  {(selectedWebsite?.website_name || "?")[0].toUpperCase()}
                </div>
                <span className="text-sm font-medium">{selectedWebsite?.website_name}</span>
                <span className="text-xs text-gray-400">{selectedWebsite?.website_url}</span>
                <button onClick={() => { setSelectedWebsiteId(""); setWebsiteSearch(""); }} className="ml-auto text-gray-400 hover:text-gray-600">x</button>
              </div>
            ) : (
              <div>
                <input
                  type="text"
                  value={websiteSearch}
                  onChange={(e) => setWebsiteSearch(e.target.value)}
                  placeholder="Search by name or URL..."
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-violet-200 focus:border-violet-400 focus:outline-none"
                  autoFocus
                />
                <div className="mt-1 max-h-40 overflow-y-auto border rounded-lg bg-white divide-y">
                  {isLoadingWebsites ? (
                    <div className="px-3 py-4 text-center text-sm text-gray-400">Loading websites...</div>
                  ) : filteredWebsites.length === 0 ? (
                    <div className="px-3 py-4 text-center text-sm text-gray-400">No matching websites</div>
                  ) : (
                    filteredWebsites.slice(0, 20).map((w) => (
                      <button
                        key={w.website_id}
                        onClick={() => { setSelectedWebsiteId(w.website_id); setWebsiteSearch(""); }}
                        className="w-full text-left px-3 py-2 hover:bg-violet-50 transition-colors flex items-center gap-2"
                      >
                        <div className="w-5 h-5 rounded bg-amber-100 flex items-center justify-center text-xs font-bold text-amber-700 flex-shrink-0">
                          {(w.website_name || "?")[0].toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{w.website_name}</div>
                          <div className="text-xs text-gray-400 truncate">{w.website_url}</div>
                        </div>
                      </button>
                    ))
                  )}
                  {filteredWebsites.length > 20 && (
                    <div className="px-3 py-2 text-xs text-gray-400 text-center">
                      {filteredWebsites.length - 20} more — refine your search
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">Initial test cases</label>
            <div className="flex gap-1">
              <button onClick={() => setInputMode("empty")} className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${inputMode === "empty" ? "bg-violet-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                Start empty
              </button>
              <button onClick={() => setInputMode("json")} className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${inputMode === "json" ? "bg-violet-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                Paste JSON
              </button>
            </div>
          </div>

          {inputMode === "json" ? (
            <>
              <textarea
                value={newTestCasesText}
                onChange={(e) => setNewTestCasesText(e.target.value)}
                rows={10}
                className="w-full p-3 border rounded-lg text-sm font-mono focus:ring-2 focus:ring-violet-200 focus:border-violet-400 focus:outline-none resize-y"
                placeholder={`[\n  {\n    "id": "test_001",\n    "name": "Test case name",\n    "category": "Category",\n    "url_placeholder": "root_url",\n    "activated": true,\n    "items": [\n      {"item_type": "agent_multistep", "title": "Step 1", "objective": "..."},\n      {"item_type": "verify", "title": "Check", "objective": "..."}\n    ]\n  }\n]`}
              />
              <span className="text-xs text-gray-400 mt-0.5">{newTestCasesText.length} chars</span>
            </>
          ) : (
            <div className="p-4 bg-gray-50 rounded-lg border text-sm text-gray-500">
              A starter test case with one action step and one verify step will be created. You can edit it visually after creation.
            </div>
          )}
        </div>

        <div className="flex gap-2">
          {inputMode === "json" ? (
            <button
              onClick={handleCreate}
              disabled={isCreating || !newTestCasesText.trim() || (mode === "website" && !selectedWebsiteId)}
              className="px-5 py-2 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {isCreating ? "Creating..." : "Create Test Cases"}
            </button>
          ) : (
            <button
              onClick={handleCreateEmpty}
              disabled={isCreating || (mode === "website" && !selectedWebsiteId)}
              className="px-5 py-2 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {isCreating ? "Creating..." : "Create with Starter Template"}
            </button>
          )}
          <button onClick={handleCancel} className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50 transition-colors">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-3 pt-2">
      {!hasBaseTestCases && (
        <button
          onClick={() => setMode("base")}
          className="flex-1 py-3 border-2 border-dashed border-violet-300 text-violet-600 rounded-xl text-sm font-medium hover:bg-violet-50 hover:border-violet-400 transition-colors flex items-center justify-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          Add Base Test Cases
        </button>
      )}
      <button
        onClick={() => setMode("website")}
        disabled={isLoadingWebsites}
        className="flex-1 py-3 border-2 border-dashed border-amber-300 text-amber-700 rounded-xl text-sm font-medium hover:bg-amber-50 hover:border-amber-400 transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
        Add Website Test Cases
        {isLoadingWebsites && <span className="text-xs">(loading...)</span>}
      </button>
    </div>
  );
};

export default AddWebsiteTestCasesSection;
